import { useQuery } from 'react-query';
import axios from 'axios';

import { backendRequestHeaders, getBackendBaseUrl } from '@/api/backendAuth';
import { useAppSelector } from '@/app/hooks';
import { ActivityLog } from '@/models/ActivityLogsByWorkspace';
import { useAuth } from '@/modules/Auth/hooks/useAuth';
import { selectCurrentWorkspace } from '@/modules/Dashboard/Workspaces/workspaceSlice';

import queryKeys from '../../modules/shared/query/queryKeys';

import {
  type BackendActivityLogsResponse,
  mapRowToActivityLog,
  sortActivityLogsNewestFirst,
} from './useGetActivityLogsByWorkspace';

/**
 * Logs for a single audience member (email), filtered on the server.
 * Avoids the bug where the workspace feed is capped (e.g. 500 newest rows globally) and
 * client-side email filtering leaves only recent days in the chart.
 */
export function useGetActivityLogsForMemberEmail(memberEmail: string | undefined) {
  const { user } = useAuth();
  const workspace = useAppSelector(selectCurrentWorkspace);
  const normalized = memberEmail?.trim().toLowerCase();

  return useQuery<ActivityLog[]>({
    queryKey: [queryKeys.activityLogs, workspace?.id, 'member-email', normalized],
    queryFn: async () => {
      const token = user?.signInUserSession.idToken.jwtToken;
      const workspaceId = workspace?.id ?? '';
      const response = await axios.get<BackendActivityLogsResponse>(
        `${getBackendBaseUrl()}/workspaces/${workspaceId}/activity-logs`,
        {
          headers: backendRequestHeaders(token),
          params: {
            userEmail: normalized,
            sinceDays: 30,
            limit: 3000,
          },
        },
      );
      const rows = response.data.logs ?? [];
      return sortActivityLogsNewestFirst(rows.map(mapRowToActivityLog));
    },
    enabled: !!workspace?.id && !!user?.signInUserSession?.idToken.jwtToken && !!normalized && normalized.length > 0,
    staleTime: 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  });
}
